import React, { useCallback, useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { useCreateSetupIntent } from "../../api/mutations/useCreateSetupIntent";
import { useSavePayment } from "../../api/mutations/useSavePayment";
import { useTranslation } from "react-i18next";
import {
  logPaymentError,
  getPaymentErrorSource,
  getPaymentErrorMessage,
} from "../../utils/paymentErrorLogger";

const CARD_STYLE = {
  base: { fontSize: "16px" as const, color: "#000", "::placeholder": { color: "#999" } },
  invalid: { color: "red" as const },
};

interface AddPaymentMethodModalProps {
  show: boolean;
  onHide: () => void;
  userId: string;
  onSuccess: () => void;
}

const AddPaymentMethodModal: React.FC<AddPaymentMethodModalProps> = ({
  show,
  onHide,
  userId,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [billingAddress, setBillingAddress] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const { mutateAsync: createSetupIntent, data: setupData, isPending: loadingSetup } = useCreateSetupIntent();
  const { mutate: savePaymentMethod } = useSavePayment({
    onSuccess: () => {
      onSuccess();
      onHide();
      setError("");
      setBillingAddress("");
    },
    onError: (err: unknown) => {
      const msg = getPaymentErrorMessage(err);
      logPaymentError({
        flow: "add-payment-method",
        errorSource: getPaymentErrorSource(err),
        message: msg,
        error: err,
        userId,
        responseStatus:
          err && typeof err === "object" && "response" in err
            ? (err as { response?: { status?: number } }).response?.status
            : undefined,
        responseData:
          err && typeof err === "object" && "response" in err
            ? (err as { response?: { data?: unknown } }).response?.data
            : undefined,
      });
      setError(msg || t("payment.saveCardFailed"));
      setLoading(false);
    },
  });

  useEffect(() => {
    if (show && userId) {
      setError("");
      createSetupIntent({ userId }).catch((err: unknown) => {
        const msg = getPaymentErrorMessage(err) || t("payment.loadFormFailed");
        logPaymentError({
          flow: "add-payment-method",
          errorSource: getPaymentErrorSource(err),
          message: msg,
          error: err,
          userId,
          responseStatus:
            err && typeof err === "object" && "response" in err
              ? (err as { response?: { status?: number } }).response?.status
              : undefined,
          responseData:
            err && typeof err === "object" && "response" in err
              ? (err as { response?: { data?: unknown } }).response?.data
              : undefined,
        });
        setError(msg);
      });
    }
  }, [show, userId, t]); // eslint-disable-line react-hooks/exhaustive-deps -- only when modal opens

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!stripe || !elements || !setupData?.clientSecret || !setupData?.customerId) {
        const msg = t("payment.formNotReady");
        logPaymentError({
          flow: "add-payment-method",
          errorSource: "frontend",
          message: msg,
          userId,
        });
        setError(msg);
        return;
      }
      setLoading(true);
      setError("");
      const cardNumberElement = elements.getElement(CardNumberElement);
      if (!cardNumberElement) {
        const msg = t("payment.cardInputNotFound");
        logPaymentError({
          flow: "add-payment-method",
          errorSource: "frontend",
          message: msg,
          userId,
        });
        setError(msg);
        setLoading(false);
        return;
      }
      try {
        const { paymentMethod, error: pmError } = await stripe.createPaymentMethod({
          type: "card",
          card: cardNumberElement,
        });
        if (pmError) {
          const msg = pmError.message || t("payment.validationFailed");
          logPaymentError({
            flow: "add-payment-method",
            errorSource: "stripe",
            message: msg,
            error: pmError,
            userId,
          });
          setError(msg);
          setLoading(false);
          return;
        }
        if (!paymentMethod?.id) {
          const msg = t("payment.createPaymentMethodFailed");
          logPaymentError({
            flow: "add-payment-method",
            errorSource: "stripe",
            message: msg,
            userId,
          });
          setError(msg);
          setLoading(false);
          return;
        }
        const { setupIntent, error: confirmError } = await stripe.confirmCardSetup(setupData.clientSecret, {
          payment_method: paymentMethod.id,
        });
        if (confirmError) {
          const msg = confirmError.message || t("payment.setupFailed");
          logPaymentError({
            flow: "add-payment-method",
            errorSource: "stripe",
            message: msg,
            error: confirmError,
            userId,
          });
          setError(msg);
          setLoading(false);
          return;
        }
        if (setupIntent?.status === "succeeded") {
          savePaymentMethod({
            userId,
            customerId: setupData.customerId,
            paymentMethodId: paymentMethod.id,
            billingAddress,
            brand: paymentMethod.card?.brand ?? "",
            last4: paymentMethod.card?.last4 ?? "",
          });
        } else {
          const msg = t("payment.setupIncomplete");
          logPaymentError({
            flow: "add-payment-method",
            errorSource: "stripe",
            message: msg,
            userId,
            requestPayload: { setupIntentStatus: setupIntent?.status },
          });
          setError(msg);
          setLoading(false);
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : t("payment.genericError");
        logPaymentError({
          flow: "add-payment-method",
          errorSource: getPaymentErrorSource(err),
          message: msg,
          error: err,
          userId,
        });
        setError(msg);
        setLoading(false);
      }
    },
    [stripe, elements, setupData, userId, billingAddress, savePaymentMethod, t]
  );

  const handleClose = () => {
    if (!loading && !loadingSetup) onHide();
  };

  return (
    <Modal centered show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>{t("payment.addMethod.title")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loadingSetup ? (
          <p className="text-muted">{t("payment.addMethod.loading")}</p>
        ) : (
          <Form onSubmit={handleSubmit}>
            <div className="mb-3">
              <Form.Label>Card number</Form.Label>
              <div className="form-control">
                <CardNumberElement
                  options={{
                    style: { base: CARD_STYLE.base, invalid: CARD_STYLE.invalid },
                  }}
                />
              </div>
            </div>
            <div className="row g-2">
              <div className="col-6">
                <Form.Label>Expiry</Form.Label>
                <div className="form-control">
                  <CardExpiryElement options={{ style: { base: CARD_STYLE.base } }} />
                </div>
              </div>
              <div className="col-6">
                <Form.Label>CVC</Form.Label>
                <div className="form-control">
                  <CardCvcElement options={{ style: { base: CARD_STYLE.base } }} />
                </div>
              </div>
            </div>
            <div className="mb-3">
              <Form.Label>Billing address (optional)</Form.Label>
              <Form.Control
                type="text"
                placeholder="Billing address"
                value={billingAddress}
                onChange={(e) => setBillingAddress(e.target.value)}
              />
            </div>
            {error && (
              <div className="alert alert-danger py-2 px-3 mb-3" role="alert">
                {error}
              </div>
            )}
            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={handleClose} disabled={loading}>
                {t("payment.addMethod.cancel")}
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={!stripe || loading || !setupData?.clientSecret}
              >
                {loading ? t("payment.addMethod.saving") : t("payment.addMethod.saveCard")}
              </Button>
            </div>
          </Form>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default AddPaymentMethodModal;
